import { NgModule, APP_INITIALIZER } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { MenubarModule } from 'primeng/menubar';
import { MenuModule } from 'primeng/menu';
import { RippleModule } from 'primeng/ripple';
import { MessageService, SharedModule } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CaptchaModule } from 'primeng/captcha';
import { ToastModule } from 'primeng/toast';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { AuthModule, AuthClientConfig } from '@auth0/auth0-angular';
import { ConfigService } from './services/config-service/config.service';
import { AppComponent } from './components/app-component/app.component';
import { FooterComponent } from './components/footer-component/footer.component';
import { HeaderComponent } from './components/header-component/header.component';
import { MainRoutingModule } from './modules/main-routing.module';
import { ComponentsModule } from './modules/components.module';
import { LoginProcessComponent } from './routes/login-process/login-process.component';
import { EmailComponent } from './routes/login-process/email/email.component';
import { PasswordComponent } from './routes/login-process/password/password.component';
import { ForgotPasswordEmailComponent } from './routes/login-process/forgot-password-email/forgot-password-email.component';
import { AccountConfirmedComponent } from './routes/login-process/account-confirmed/account-confirmed.component';
import { ChangePasswordComponent } from './routes/login-process/change-password/change-password.component';
import { EmailSentComponent } from './routes/login-process/email-sent/email-sent.component';
import { CreatePasswordComponent } from './routes/login-process/create-password/create-password.component';
import { LinkExpiredComponent } from './routes/login-process/link-expired/link-expired.component';
import { LoginModalComponent } from './components/login-modal/login-modal.component';
import { MarketPlaceQuoteRequestService } from './services/market-place-quote-request-service/market-place-quote-request.service';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MultiSelectModule } from 'primeng/multiselect';
import { LoginService } from './services/login-service/login.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TimeoutModalComponent } from './components/timeout-modal/timeout-modal.component';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { FeedbackModalComponent } from './components/app-feedback-modal-content/app-feedback-modal-content.component';
import { DropdownModule } from 'primeng/dropdown';
import { AccessDeniedComponent } from './routes/login-process/access-denied/access-denied.component';
import { SignUpComponent } from './routes/login-process/sign-up/sign-up.component';
import { SearchInventoryRoutingModule } from './modules/search-inventory-routing.module';
import { SignUpSuccessComponent } from './routes/login-process/sign-up-success/sign-up-success.component';
import { MixpanelService } from './services/mixpanel-service/mixpanel.service';
import { ResourceCenterModalComponent } from './components/resource-center-modal/resource-center-modal.component';
import { AvatarModule } from 'primeng/avatar';
import { CardModule } from 'primeng/card';
import { FaqsComponent } from './components/faqs/faqs.component';
import { SubmitQuoteDialogComponent } from './components/submit-quote-dialog/submit-quote-dialog.component';
import { RadioButtonModule } from 'primeng/radiobutton';
import { AutoCompleteModule } from 'primeng/autocomplete';

// Function to initialize the configuration at runtime
export function configInitializer(
  configService: ConfigService,
  authClientConfig: AuthClientConfig
): () => Promise<void> {
  return async () => {
    console.log('Initializing configuration...');
    await configService.loadAsyncConfig();
    console.log('Configuration loaded:', {
      domain: configService.auth0domain,
      clientId: configService.auth0clientId,
      audience: configService.auth0audience,
    });

    authClientConfig.set({
      domain: configService.auth0domain,
      clientId: configService.auth0clientId,
      authorizationParams: {
        audience: configService.auth0audience,
        redirect_uri: window.location.origin,
      },
    });
    console.log('AuthClientConfig set successfully.');
  };
}

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    HeaderComponent,
    LoginProcessComponent,
    EmailComponent,
    PasswordComponent,
    ForgotPasswordEmailComponent,
    AccountConfirmedComponent,
    ChangePasswordComponent,
    EmailSentComponent,
    CreatePasswordComponent,
    LinkExpiredComponent,
    LoginModalComponent,
    TimeoutModalComponent,
    FeedbackModalComponent,
    AccessDeniedComponent,
    SignUpComponent,
    SignUpSuccessComponent,
    ResourceCenterModalComponent,
    FaqsComponent,
    SubmitQuoteDialogComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    MainRoutingModule,
    MenubarModule,
    RippleModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
    BrowserAnimationsModule,
    CaptchaModule,
    ToastModule,
    ComponentsModule,
    DynamicDialogModule,
    FlexLayoutModule,
    MenuModule,
    MultiSelectModule,
    ProgressSpinnerModule,
    NgIdleKeepaliveModule.forRoot(),
    DropdownModule,
    SearchInventoryRoutingModule,
    AvatarModule,
    CardModule,
    RadioButtonModule,
    AutoCompleteModule,
    HttpClientModule,
    AuthModule.forRoot({
      domain: '',
      clientId: '',
      authorizationParams: {
        audience: '',
        redirect_uri: window.location.origin,
      },
    }),
  ],
  providers: [
    MessageService,
    MarketPlaceQuoteRequestService,
    DialogService,
    ConfigService,
    LoginService,
    MixpanelService,
    {
      provide: APP_INITIALIZER,
      useFactory: configInitializer,
      deps: [ConfigService, AuthClientConfig],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  entryComponents: [LoginModalComponent],
})
export class AppModule {}
